/**
 * Platform Admin Hook
 *
 * Checks if the current user has platform-level admin access.
 * This is SEPARATE from team-level roles (owner/admin/member).
 *
 * Platform admin is determined by: /users/{uid}.isPlatformAdmin === true
 *
 * This grants access to:
 * - Admin tab (team management)
 * - Admin Cloud Functions (adminListTeams, adminSetTeamPlan, etc.)
 *
 * Migration: Also accepts legacy /users/{uid}.role === 'admin' for backward
 * compatibility during migration period.
 *
 * NOTE: This hook now uses GlobalAdminContext for a single source of truth.
 * The context is mounted once near the root and shares the admin status
 * with all consumers, avoiding duplicate Firestore reads/subscriptions.
 */

import { useGlobalAdminContext } from '@/contexts/GlobalAdminContext';

export function usePlatformAdmin(): { isPlatformAdmin: boolean; isLoading: boolean } {
  // Use the global context for single source of truth
  return useGlobalAdminContext();
}

// Re-export with old name for backward compatibility during migration
// TODO: Remove after all usages are updated
export function useGlobalAdmin(): { isGlobalAdmin: boolean; isLoading: boolean } {
  const { isPlatformAdmin, isLoading } = usePlatformAdmin();
  return { isGlobalAdmin: isPlatformAdmin, isLoading };
}
